/**
 * กำหนดวันครบของเช็คลิสต์รับพนักงานใหม่ — "อีกกี่วันต้องเซ็น/ต้องส่งเอกสาร"
 *
 * `onboarding_items.due_days` = ต้องเสร็จภายในกี่วัน **นับจากวันเริ่มงาน**
 * (0 = ครบกำหนดวันเริ่มงานพอดี · NULL = ไม่มีกำหนด)
 *
 * ⭐ ไม่มีวันเริ่มงาน = คำนวณไม่ได้ → คืน 'no_start_date' ให้หน้าจอบอกตรง ๆ
 * ห้ามเดาวันแทนคน (ตระกูลเดียวกับ [[feedback_leaver_eligibility_by_period_not_status]])
 * ⚠️ ทุกฟังก์ชันรับ `today` เข้ามาเสมอ ไม่เรียก new Date() เอง — เทสจะได้ไม่เน่าตามเวลา
 */

/** สถานะความด่วนของรายการที่ยังไม่เสร็จ */
export type OnboardingDueState =
  | 'done'            // เสร็จแล้ว ไม่ต้องสนใจกำหนด
  | 'no_due'          // ไม่ได้ตั้งกำหนดไว้
  | 'no_start_date'   // ตั้งกำหนดไว้ แต่พนักงานยังไม่มีวันเริ่มงาน → คำนวณไม่ได้
  | 'overdue'         // เลยกำหนดแล้ว
  | 'due_today'       // ครบกำหนดวันนี้
  | 'due_soon'        // เหลือ 1-3 วัน
  | 'upcoming';       // เหลือมากกว่า 3 วัน

/** เหลือ ≤ วันนี้ ถือว่าด่วน (ใช้ทั้งการนับยอดและการเรียงลำดับ) */
export const DUE_SOON_DAYS = 3;

/** บวกวันแบบปฏิทิน UTC ล้วน — ไม่แตะ timezone ของเครื่อง (ดู feedback getUTCDay+07:00) */
export function addDaysISO(dateISO: string, days: number): string {
  const [y, m, d] = dateISO.slice(0, 10).split('-').map(Number);
  const t = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) + days * 86400000;
  return new Date(t).toISOString().slice(0, 10);
}

/** จำนวนวันจาก a ถึง b (b - a) นับเป็นวันปฏิทิน */
export function daysBetweenISO(a: string, b: string): number {
  const pa = a.slice(0, 10).split('-').map(Number);
  const pb = b.slice(0, 10).split('-').map(Number);
  const ta = Date.UTC(pa[0] ?? 1970, (pa[1] ?? 1) - 1, pa[2] ?? 1);
  const tb = Date.UTC(pb[0] ?? 1970, (pb[1] ?? 1) - 1, pb[2] ?? 1);
  return Math.round((tb - ta) / 86400000);
}

export interface OnboardingDueInput {
  /** วันเริ่มงานของพนักงาน (null = ยังไม่ระบุ) */
  startDate?: string | null;
  /** กำหนดกี่วันนับจากวันเริ่มงาน (null/undefined = ไม่มีกำหนด) */
  dueDays?: number | null;
  /** รายการนี้เสร็จแล้วหรือยัง */
  done: boolean;
}

export interface OnboardingDueResult {
  state: OnboardingDueState;
  /** วันครบกำหนด (null เมื่อคำนวณไม่ได้) */
  dueDate: string | null;
  /** เหลืออีกกี่วัน — ติดลบ = เลยกำหนดมาแล้วกี่วัน (null เมื่อคำนวณไม่ได้) */
  daysLeft: number | null;
}

export function onboardingDue(input: OnboardingDueInput, today: string): OnboardingDueResult {
  const none = { dueDate: null, daysLeft: null };
  if (input.done) return { state: 'done', ...none };
  if (input.dueDays === null || input.dueDays === undefined) return { state: 'no_due', ...none };
  if (!input.startDate) return { state: 'no_start_date', ...none };

  const dueDate = addDaysISO(input.startDate, input.dueDays);
  const daysLeft = daysBetweenISO(today, dueDate);
  const state: OnboardingDueState =
    daysLeft < 0 ? 'overdue'
      : daysLeft === 0 ? 'due_today'
        : daysLeft <= DUE_SOON_DAYS ? 'due_soon'
          : 'upcoming';
  return { state, dueDate, daysLeft };
}

/** ด่วนกว่า = เลขน้อยกว่า ใช้เรียงรายการ "ต้องจัดการก่อน" ขึ้นบนสุด */
export function dueUrgencyRank(state: OnboardingDueState): number {
  switch (state) {
    case 'overdue': return 0;
    case 'due_today': return 1;
    case 'due_soon': return 2;
    case 'no_start_date': return 3;   // ตั้งกำหนดไว้แต่กรอกวันเริ่มงานไม่ครบ = ต้องไปแก้ข้อมูล
    case 'upcoming': return 4;
    case 'no_due': return 5;
    case 'done': return 6;
  }
}

/** คำไทยสั้น ๆ สำหรับป้ายบนหน้าจอ (สีอย่างเดียวไม่พอ ต้องมีข้อความเสมอ) */
export function dueLabel(r: OnboardingDueResult): string {
  switch (r.state) {
    case 'overdue': return `เลยกำหนด ${Math.abs(r.daysLeft ?? 0)} วัน`;
    case 'due_today': return 'ครบกำหนดวันนี้';
    case 'due_soon': return `เหลือ ${r.daysLeft} วัน`;
    case 'upcoming': return `เหลือ ${r.daysLeft} วัน`;
    case 'no_start_date': return 'ยังไม่ระบุวันเริ่มงาน';
    case 'no_due': return 'ไม่มีกำหนด';
    case 'done': return 'เสร็จแล้ว';
  }
}
